export type WSLDSCacheEntry = {
  cacheHash: string;
  rows: { id: string; value: { [key: string]: string } }[];
};

type IDBStoreRowsCacheEntry = {
  id: string;
  rows: { id: string; value: { [key: string]: string } }[];
};
type IDBStoreMetaCacheEntry = {
  id: string;
  cacheHash: string;
  updatedAt: number;
};

const THROTTLE_TIME = 1000 * 2;

const reqToPromise = <T>(req: IDBRequest<T> | null): Promise<T> => {
  return new Promise((resolve, reject) => {
    if (!req) return resolve(null as unknown as T);
    req.addEventListener("success", () => {
      resolve(req.result);
    });
    req.addEventListener("error", () => {
      reject(req.error);
    });
  });
};
const idbTransaction = (
  db: IDBDatabase,
  storeNames: string[],
  mode: IDBTransactionMode
) => {
  const transaction = db.transaction(storeNames, mode);
  return [
    transaction,
    ...storeNames.map((name) => transaction.objectStore(name)),
  ] as const;
};

const ldsIndexedDBPromise = new Promise<IDBDatabase | null>((resolve) => {
  const dBOpenRequest = window.indexedDB.open("lds", 4);
  dBOpenRequest.addEventListener("upgradeneeded", () => {
    const db = dBOpenRequest.result;
    db.createObjectStore("rowsCache", {
      keyPath: "id",
    });
    db.createObjectStore("metaCache", {
      keyPath: "id",
    });
  });
  dBOpenRequest.addEventListener("success", () => {
    resolve(dBOpenRequest.result);
    const db = dBOpenRequest.result;
  });
  dBOpenRequest.addEventListener("error", () => {
    console.error("error opening database:", dBOpenRequest.error);
    resolve(null);
  });
});

ldsIndexedDBPromise.then((db) => {
  if (!db) return;
  const [, storeRows, storeMeta] = idbTransaction(
    db,
    ["rowsCache", "metaCache"],
    "readwrite"
  );
  const twoWeekAgo = Date.now() - 1000 * 60 * 60 * 24 * 14;
  const req = storeMeta.openCursor();
  req.addEventListener("success", () => {
    const cursor = req.result;
    if (cursor) {
      const entry = cursor.value as IDBStoreMetaCacheEntry;
      if (entry.updatedAt < twoWeekAgo) {
        console.warn("Deleting old cache entry:", entry.id);
        storeMeta.delete(entry.id);
        storeRows.delete(entry.id);
      }
      cursor.continue();
    }
  });
  req.addEventListener("error", () => {
    console.error("Error during cursor operation:", req.error);
  });
});

class LDSIndexedDBCache {
  jobs = new Map<
    string,
    {
      value: WSLDSCacheEntry | null;
    }
  >();
  lastSetPromise = new Map<string, Promise<void>>();
  lastSetTime = new Map<string, number>();
  async get(id: string): Promise<WSLDSCacheEntry | null> {
    const db = await ldsIndexedDBPromise;
    if (!db) return null;
    return new Promise(async (resolve) => {
      const [, storeRows, storeMeta] = idbTransaction(
        db,
        ["rowsCache", "metaCache"],
        "readonly"
      );
      Promise.all([
        reqToPromise(
          storeRows.get(id) as IDBRequest<IDBStoreRowsCacheEntry | undefined>
        ),
        reqToPromise(
          storeMeta.get(id) as IDBRequest<IDBStoreMetaCacheEntry | undefined>
        ),
      ])
        .then(([cacheRows, cacheMeta]) => {
          if (!cacheRows || !cacheMeta) {
            resolve(null);
          }
          resolve({
            cacheHash: cacheMeta?.cacheHash || "0".repeat(16),
            rows: cacheRows?.rows || [],
          });
        })
        .catch((error) => {
          console.error("Error getting cache entry:", error);
          resolve(null);
        });
    });
  }
  async set(id: string, entry: WSLDSCacheEntry | null): Promise<void> {
    const currentTime = Date.now();
    this.lastSetTime.set(id, currentTime);
    const setPromise = (async () => {
      const db = await ldsIndexedDBPromise;
      if (!db) return;
      return new Promise<void>(async (resolve) => {
        const [, storeRows, storeMeta] = idbTransaction(
          db,
          ["rowsCache", "metaCache"],
          "readwrite"
        );
        const reqWriteRows = entry
          ? storeRows.put({
              id,
              rows: entry.rows,
            } satisfies IDBStoreRowsCacheEntry)
          : null;
        const reqWriteMeta = entry
          ? storeMeta.put({
              id,
              cacheHash: entry.cacheHash,
              updatedAt: currentTime,
            } satisfies IDBStoreMetaCacheEntry)
          : storeMeta.put({
              id,
              cacheHash: await reqToPromise(
                storeMeta.get(id) as IDBRequest<
                  IDBStoreMetaCacheEntry | undefined
                >
              ).then((meta) => meta?.cacheHash || "0".repeat(16)),
              updatedAt: currentTime,
            } satisfies IDBStoreMetaCacheEntry);
        Promise.all([reqToPromise(reqWriteRows), reqToPromise(reqWriteMeta)])
          .catch((e) => {
            console.error("Error setting cache entry:", e);
          })
          .finally(() => resolve());
      });
    })();
    this.lastSetPromise.set(id, setPromise);
  }
  async setThrottled(id: string, entry: WSLDSCacheEntry | null) {
    const currentTime = Date.now();
    const lastSetTime = this.lastSetTime.get(id) || 0;
    const job = this.jobs.get(id);

    if (job) {
      job.value = entry;
    } else {
      const job = { value: entry };
      Promise.all([
        new Promise((resolve) =>
          setTimeout(
            resolve,
            currentTime - lastSetTime > THROTTLE_TIME ? 0 : THROTTLE_TIME
          )
        ),
        this.lastSetPromise.get(id) || Promise.resolve(),
      ]).then(async () => {
        this.jobs.delete(id);
        await this.set(id, job.value);
      });
      this.jobs.set(id, job);
    }
  }
}
export const ldsIndexedDBCache = new LDSIndexedDBCache();
